import React, { useState } from "react";
import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Grid } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getPayrollByEmployeeId } from "../../services/payroll.service";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import logo from "../../assets/images/Lamynaals_Tech_Logo-01 5.png";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { isAdminRole } from "../../utils/roles";

const DownloadPayslipButton = ({ employeeData, style }) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const { accessToken } = useAuth();

    // Check if user is admin
    const isAdmin = () => {
        if (accessToken) {
            const decoded = jwtDecode(accessToken);
            return isAdminRole(decoded?.role);
        }
        return false;
    };

    // Default to current date
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const currentMonthName = monthNames[currentDate.getMonth()];

    const [selectedMonth, setSelectedMonth] = useState(currentMonthName);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // Generate year options (current year +/- 5 years)
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
        years.push(i);
    }

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDownload = () => {
        handleClose();
        generatePDF(selectedMonth, selectedYear);
    };

    const convertImgToBase64 = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.setAttribute('crossOrigin', 'anonymous');
            img.src = url;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                // Scale up for better quality if needed, but defaults usually fine for logo
                canvas.width = img.width || 200;
                canvas.height = img.height || 100;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataURL = canvas.toDataURL("image/png");
                resolve(dataURL);
            };
            img.onerror = (error) => {
                console.error("Image load error", error);
                // Resolve with null so we can continue without logo
                resolve(null);
            };
        });
    };

    const generatePDF = async (overrideMonth, overrideYear) => {
        setLoading(true);
        try {
            // Determine target month/year
            const targetMonth = overrideMonth || selectedMonth;
            const targetYear = overrideYear || selectedYear;

            // Fetch payroll data with filters
            let payrollData = null;
            try {
                // We pass month and year as query params to the service (which appends them to URL string)
                // Note: The service function `getPayrollByEmployeeId` might need update to handle params if it doesn't already?
                // Actually, the service `getPayrollByEmployeeId` typically takes just ID.
                // We need to pass query params manually or update service.
                // Assuming we updated backend to accept ?month=X&year=Y
                // We should append query string to ID or update service.
                // For now, let's append to the ID argument if the service takes string URL?
                // Service: axiosHttp.get(`/ lmt / payroll / ${ employeeId } `);
                // Helper: `${ employeeData._id }?month = ${ targetMonth }& year=${ targetYear } ` works if axios doesn't encode / but it usually does.
                // Better to rely on the backend filtering the array returned IF we can't change service easily here?
                // BUT I UPDATED THE BACKEND TO FILTER!
                // So I should call: /lmt/payroll/ID?month=...&year=...
                // If I can't change service signature immediately, I can pass `${ id }?month =...` hack, but likely to fail URL encoding.
                // Let's assume I check the service again.
                // Service: `return axiosHttp.get(`/lmt/payroll/${employeeId}`); `
                // I can pass `employeeId` as `${ employeeData._id }?month = ${ targetMonth }& year=${ targetYear } ` IF axios doesn't double encode.
                // Or better, let's just do client side filtering if the service update file wasn't requested?
                // WAIT, I modified `dao`, `service`, `controller` on BACKEND.
                // I did NOT modify frontend `services / payroll.service.js`.
                // So I should modify frontend service OR do the query param hack.
                // I'll update the frontend service to be clean.

                // Oops, I can't update frontend service in THIS tool call (single replace).
                // I will assume for this step that I'll resort to client-side filtering as fallback OR pass via hacked ID string?
                // No, I'll assume I will update the service in next step or use the filtering here if I fetch ALL.
                // Since `getPayrollByEmployeeId` returns ALL by default (if no query params), I can just fetch all and filter here!
                // Wait, I updated backend to return filtered list IF params provided.
                // But frontend service doesn't pass params.
                // So backend receives NO params. Returns ALL.
                // So I can filter here on client side! simpler!
                // AND efficient enough for single user payroll history.

                const res = await getPayrollByEmployeeId(employeeData._id);
                console.log("PAYROLL RESPONSE:", res);

                // Handling response structure (res.data or res.data.data)
                const allPayrolls = (res.data && Array.isArray(res.data)) ? res.data
                    : (res.data?.data && Array.isArray(res.data.data)) ? res.data.data
                        : [];

                if (allPayrolls.length > 0) {
                    const targetM = targetMonth.toString().trim().toLowerCase();
                    const targetY = targetYear.toString().trim();
                    console.log("TARGET REQUEST:", targetM, targetY);

                    // Map full month names to numbers strings
                    const monthMap = {
                        "january": "1", "february": "2", "march": "3", "april": "4", "may": "5", "june": "6",
                        "july": "7", "august": "8", "september": "9", "october": "10", "november": "11", "december": "12",
                        "jan": "1", "feb": "2", "mar": "3", "apr": "4", "jun": "6", "jul": "7", "aug": "8", "sep": "9", "oct": "10", "nov": "11", "dec": "12"
                    };

                    const normalizedTarget = monthMap[targetM] || targetM;

                    // Filter
                    const filtered = allPayrolls.find(p => {
                        if (!p.month || !p.year) return false;

                        const dbMonth = p.month.toString().trim().toLowerCase();
                        const dbYear = p.year.toString().trim();

                        // Match against either raw target or mapped target
                        const monthMatch = (dbMonth === normalizedTarget) || (dbMonth === targetM);
                        const yearMatch = dbYear === targetY;

                        console.log(`Checking DB(${dbMonth}, ${dbYear}) vs Target(${normalizedTarget}, ${targetY}) => Match ? ${monthMatch && yearMatch} `);

                        return monthMatch && yearMatch;
                    });

                    console.log("filtered", filtered);

                    if (filtered) {
                        payrollData = filtered;
                    }
                }
            } catch (err) {
                console.error("Error fetching payroll", err);
            }

            if (!payrollData) {
                ToastError(`No receipt available for ${targetMonth} ${targetYear} `);
                setLoading(false);
                return;
            }

            // Check for Approved status (skip for admins)
            if (!isAdmin()) {
                const validStatuses = ["Success"];
                if (!validStatuses.includes(payrollData.status)) {
                    ToastError("Your Payslip is not Approved yet. Please check back later.");
                    setLoading(false);
                    return;
                }
            }

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15;

            // Helper function to convert number to words (Indian format)
            const numberToWords = (num) => {
                const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
                const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

                if (num === 0) return 'Zero';

                const convertHundreds = (n) => {
                    let str = '';
                    if (n > 99) {
                        str += ones[Math.floor(n / 100)] + ' Hundred ';
                        n %= 100;
                    }
                    if (n > 19) {
                        str += tens[Math.floor(n / 10)] + ' ';
                        n %= 10;
                    } else if (n > 9) {
                        str += teens[n - 10] + ' ';
                        return str;
                    }
                    if (n > 0) str += ones[n] + ' ';
                    return str;
                };

                let result = '';
                const crore = Math.floor(num / 10000000);
                num %= 10000000;
                const lakh = Math.floor(num / 100000);
                num %= 100000;
                const thousand = Math.floor(num / 1000);
                num %= 1000;

                if (crore > 0) result += convertHundreds(crore) + 'Crore ';
                if (lakh > 0) result += convertHundreds(lakh) + 'Lakh ';
                if (thousand > 0) result += convertHundreds(thousand) + 'Thousand ';
                if (num > 0) result += convertHundreds(num);

                return result.trim();
            };

            // HEADER SECTION
            let currentY = 15;

            // Logo on left
            try {
                const logoData = await convertImgToBase64(logo);
                if (logoData) {
                    const imgProps = doc.getImageProperties(logoData);
                    const logoWidth = 60; // Increased size as requested
                    const logoHeight = (imgProps.height * logoWidth) / imgProps.width;
                    doc.addImage(logoData, "PNG", margin, currentY - 5, logoWidth, logoHeight);
                }
            } catch (e) {
                console.error("Logo addition failed", e);
            }

            // Payslip title on right
            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 100, 100);
            doc.text("Payslip For the Month", pageWidth - margin, currentY, { align: "right" });

            const monthIndex = isNaN(payrollData.month) ? -1 : parseInt(payrollData.month) - 1;
            const monthName = (monthIndex >= 0 && monthIndex < 12) ? monthNames[monthIndex] : payrollData.month;
            const monthYear = `${monthName} ${payrollData.year}`;

            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(50, 50, 50);
            doc.text(monthYear, pageWidth - margin, currentY + 5, { align: "right" });

            currentY = 40; // Reduced spacing for Employee Summary/Net Pay section

            // EMPLOYEE SUMMARY SECTION (Left side)
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(80, 80, 80);
            doc.text("EMPLOYEE SUMMARY", margin, currentY);

            currentY += 6;
            const labelX = margin;
            const colonX = margin + 35;
            const valueX = colonX + 5;

            const employeeDetails = [
                ["Employee Name", employeeData.name || `${employeeData.firstName || ""} ${employeeData.lastName || ""}`.trim() || "-"],
                ["Designation", employeeData.employeeDetails?.employeeRole || "-"],
                ["Employee ID", employeeData.employeeDetails?.memberID || employeeData.employeeID || "-"],
                ["Date of Joining", employeeData.joiningDate ? new Date(employeeData.joiningDate).toLocaleDateString("en-GB") : "-"],
                ["Pay Period", monthYear],
                ["Pay Date", new Date().toLocaleDateString("en-GB")],
                ["PF A/C Number", employeeData.employeeDetails?.pfNumber || "-"],
                ["UAN", payrollData.uan || "-"],
                ["ESIC Number", payrollData.esicNumber || employeeData.employeeDetails?.esicNumber || "-"]
            ];

            doc.setFontSize(8);
            employeeDetails.forEach(([label, value]) => {
                doc.setFont("helvetica", "normal");
                doc.setTextColor(100, 100, 100);
                doc.text(label, labelX, currentY);
                doc.text(":", colonX, currentY);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(50, 50, 50);
                doc.text(value, valueX, currentY);
                currentY += 5;
            });

            // NET PAY BOX (Right side - green background)
            const netPayBoxWidth = 65;
            const netPayBoxX = pageWidth - margin - netPayBoxWidth;
            const netPayBoxY = 46; // Aligned with Employee Summary content
            const netPayBoxHeight = 22;

            // Green background for net pay
            doc.setFillColor(220, 247, 227); // Light green
            doc.roundedRect(netPayBoxX, netPayBoxY, netPayBoxWidth, netPayBoxHeight, 2, 2, 'F');

            // Net pay amount
            const netSalary = Math.round(Number(payrollData.netSalary || 0));
            doc.setFontSize(16);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(34, 139, 34); // Green
            doc.text(`Rs. ${netSalary.toLocaleString('en-IN')}`, netPayBoxX + netPayBoxWidth / 2, netPayBoxY + 10, { align: "center" });

            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(80, 80, 80);
            doc.text("Employee Net Pay", netPayBoxX + netPayBoxWidth / 2, netPayBoxY + 16, { align: "center" });

            // Paid Days and LOP Days below net pay box
            const daysBoxY = netPayBoxY + netPayBoxHeight + 3;
            doc.setFillColor(255, 255, 255);
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.3);
            doc.roundedRect(netPayBoxX, daysBoxY, netPayBoxWidth / 2 - 1, 10, 1, 1, 'FD');
            doc.roundedRect(netPayBoxX + netPayBoxWidth / 2 + 1, daysBoxY, netPayBoxWidth / 2 - 1, 10, 1, 1, 'FD');

            doc.setFontSize(7);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 100, 100);
            doc.text("Paid Days", netPayBoxX + netPayBoxWidth / 4, daysBoxY + 4, { align: "center" });
            doc.text("LOP Days", netPayBoxX + 3 * netPayBoxWidth / 4, daysBoxY + 4, { align: "center" });

            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(50, 50, 50);
            doc.text(`: ${payrollData.workedDays || "30"}`, netPayBoxX + netPayBoxWidth / 4, daysBoxY + 8, { align: "center" });
            doc.text(`: ${payrollData.lopDays || "0"}`, netPayBoxX + 3 * netPayBoxWidth / 4, daysBoxY + 8, { align: "center" });

            currentY += 5; // Reduced space before table

            // EARNINGS AND DEDUCTIONS TABLE (2 columns each: Label, Amount)
            const formatAmount = (val, label) => {
                if (!label) return ""; // Blank for empty rows
                if (label === "UAN" || label === "ESIC Number") return val; // No currency formatting for IDs
                if (label === "LOP Amount" && (!val || Number(val) === 0)) return ""; // Blank if 0 for LOP
                if (val === undefined || val === null || val === "") return "Rs. 0.00";
                return `Rs. ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };

            const earningsData = [
                ["Basic", payrollData.basicSalary],
                ["House Rent Allowance", payrollData.hra],
                ["Conveyance Allowance", payrollData.conveyance],
                ["Medical Allowance", payrollData.medicalAllowance],
                ["Special Allowance", payrollData.specialAllowance],
                ["Bonus", payrollData.bonus]
            ];

            const deductionsData = [
                ["Income Tax", payrollData.incomeTax],
                ["PF", payrollData.pf],
                ["ESIC", payrollData.esicAmount],
                ["Professional Tax", payrollData.professionalTax],
                ["Other Deductions", payrollData.otherDeductions],
                ["LOP Amount", payrollData.lopAmount]
            ];

            // Build table rows
            const tableRows = [];
            const maxRows = Math.max(earningsData.length, deductionsData.length);

            for (let i = 0; i < maxRows; i++) {
                const earning = earningsData[i] || ["", ""];
                const deduction = deductionsData[i] || ["", ""];

                tableRows.push([
                    earning[0],
                    formatAmount(earning[1], earning[0]),
                    deduction[0],
                    formatAmount(deduction[1], deduction[0])
                ]);
            }

            // Add Gross Earnings and Total Deductions row
            // Gross earnings calculation (excluding UAN and ESIC Number which are at start)
            // Filter out non-monetary fields explicitly
            const parseCurrency = (val) => {
                if (!val || val === "-") return 0;
                const num = Number(String(val).replace(/,/g, ''));
                return isNaN(num) ? 0 : num;
            };

            const monetaryEarnings = earningsData.filter(([label]) => label !== "UAN" && label !== "ESIC Number");
            const grossEarningsFiltered = monetaryEarnings.reduce((sum, [, amt]) => sum + parseCurrency(amt), 0);

            const totalDeductions = deductionsData.reduce((sum, [, amt]) => sum + parseCurrency(amt), 0);

            tableRows.push([
                { content: "Gross Earnings", styles: { fontStyle: 'bold' } },
                { content: formatAmount(payrollData.grossSalary, "Gross Earnings"), styles: { fontStyle: 'bold' } },
                { content: "Total Deductions", styles: { fontStyle: 'bold' } },
                { content: formatAmount(totalDeductions, "Total Deductions"), styles: { fontStyle: 'bold' } }
            ]);

            autoTable(doc, {
                startY: currentY,
                margin: { left: margin, right: margin },
                theme: 'grid',
                head: [
                    [
                        { content: "EARNINGS", colSpan: 2, styles: { fillColor: [240, 240, 240], textColor: [50, 50, 50], fontStyle: 'bold', halign: 'left', fontSize: 9 } },
                        { content: "DEDUCTIONS", colSpan: 2, styles: { fillColor: [240, 240, 240], textColor: [50, 50, 50], fontStyle: 'bold', halign: 'left', fontSize: 9 } }
                    ],
                    [
                        { content: "", styles: { fillColor: [255, 255, 255], textColor: [80, 80, 80], fontSize: 7 } },
                        { content: "AMOUNT", styles: { fillColor: [255, 255, 255], textColor: [80, 80, 80], fontStyle: 'bold', halign: 'right', fontSize: 7 } },
                        { content: "", styles: { fillColor: [255, 255, 255], textColor: [80, 80, 80], fontSize: 7 } },
                        { content: "AMOUNT", styles: { fillColor: [255, 255, 255], textColor: [80, 80, 80], fontStyle: 'bold', halign: 'right', fontSize: 7 } }
                    ]
                ],
                body: tableRows,
                styles: {
                    lineColor: [220, 220, 220],
                    lineWidth: 0.3,
                    fontSize: 8,
                    cellPadding: 2.5,
                    textColor: [50, 50, 50],
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 68, halign: 'left' },
                    1: { cellWidth: 22, halign: 'right' },
                    2: { cellWidth: 68, halign: 'left' },
                    3: { cellWidth: 22, halign: 'right' }
                }
            });

            // TOTAL NET PAYABLE (Green background)
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY,
                margin: { left: margin, right: margin },
                theme: 'plain',
                body: [
                    [
                        {
                            content: `TOTAL NET PAYABLE\nGross Earnings - Total Deductions`,
                            styles: {
                                halign: 'left',
                                fontStyle: 'normal',
                                fontSize: 8,
                                fillColor: [220, 247, 227],
                                textColor: [50, 50, 50],
                                cellPadding: 4
                            }
                        },
                        {
                            content: formatAmount(netSalary, "Net Pay"),
                            styles: {
                                halign: 'right',
                                fontStyle: 'bold',
                                fontSize: 11,
                                fillColor: [220, 247, 227],
                                textColor: [34, 139, 34],
                                cellPadding: 4
                            }
                        }
                    ]
                ],
                styles: { lineWidth: 0 }
            });

            // Amount in Words
            currentY = doc.lastAutoTable.finalY + 5;
            const amountInWords = numberToWords(netSalary);
            doc.setFontSize(8);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(80, 80, 80);
            doc.text(`Amount in Words : Indian Rupee ${amountInWords} Only`, margin, currentY);

            // Footer
            currentY = pageHeight - 15;
            doc.setFontSize(7);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(120, 120, 120);
            doc.text("-- This document has been automatically generated by Lamynaals Payroll; therefore, a signature is not required. --",
                pageWidth / 2, currentY, { align: "center" });

            doc.save(`Payslip_${employeeData.employeeID || "EMP"}_${monthYear}.pdf`);
            ToastSuccess(`Payslip downloaded for ${monthYear}`);

        } catch (error) {
            console.error("PDF generation error:", error);
            ToastError("Failed to generate payslip");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                disabled={loading}
                size="small"
                sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    textTransform: "none",
                    fontSize: '0.8rem',
                    minWidth: 'auto',
                    borderRadius: '10px',
                    px: 2,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                    ...style
                }}
            >
                {loading ? (
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                ) : (
                    <DownloadIcon sx={{ mr: 1, fontSize: 16 }} />
                )}
                Receipt
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>Select Payslip Period</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    label="Month"
                                    onChange={(e) => setSelectedMonth(e.target.value)}
                                >
                                    {monthNames.map((month) => (
                                        <MenuItem key={month} value={month}>{month}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="Year"
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} sx={{ maxWidth: "100px", color: "error.main" }}>Cancel</Button>
                    <Button onClick={handleDownload} variant="contained" className="Add-Button" sx={{ maxWidth: "100px" }}>
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DownloadPayslipButton;
